import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DownloadIcon from '@mui/icons-material/Download';
import Typography from '@mui/material/Typography';
import { Payout, Summary, defaultSummaryData, OrderTransaction } from './billingData';
import { FullProperty } from '../../models/fullProperty';
import { downloadPayoutsCSV, fetchPayouts } from './billingServices/payouts';
import { restaurantService } from '../../services/firebase/restaurantManagementService';
import { OrderHistoryService } from '../../services/order/orderHistoryService';
import { mapCartToTransaction } from './billingServices/transactions';
import { getOrdersCsvDownloadUrl } from '../../services/functions/exportCSV';
import { toast } from 'react-toastify';
import { CircularProgress, Pagination } from '@mui/material';
import { buildCumulativeChartData } from './billingServices/chartData';
import { formatMoney } from '../../services/strings/formatters';
import { Box } from '@mui/system';
import "./billing.css"
import { TransactionsTable } from './transactionsTable';

export const BillingPage: React.FC<{ restaurant: FullProperty | null }> = ({ restaurant }) => {
  const [payouts, setPayouts] = useState<Array<Payout>>([]);
  const [summaryData, setSummaryData] = useState<Summary>(defaultSummaryData)
  const [isLoadingExportTransactionsCSV, setIsLoadingExportTransactionsCSV] = useState<boolean>(false)
  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<OrderTransaction[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!restaurant) return;
      const start = (page - 1) * PAGE_SIZE;
      const end = Math.min(start + PAGE_SIZE, restaurant.ordersCompleted.length) ;
      const batchIds = [...restaurant.ordersCompleted].reverse().slice(start, end);
      if (batchIds.length === 0) return;
      setLoadingTransactions(true);

      // fetch those carts and map to OrderTransaction
      const carts = await Promise.all(batchIds.map(id =>
        OrderHistoryService.getOrderById(id)
      ));
      const newTransactions = await Promise.all(carts
        .flatMap(f => f ? [f] : [])
        .map(async (cart) => mapCartToTransaction(cart)));
      setTransactions(newTransactions)
      setLoadingTransactions(false);
      setTotalPages(Math.ceil(restaurant.ordersCompleted.length / PAGE_SIZE))
    }
    fetchOrders()
  }, [restaurant, page]);


  useEffect(() => {
    const fetchBilling = async () => {
      if (!restaurant) { return null; }
      setPayouts(fetchPayouts(restaurant))
      const {
        lifetimePaid,
        lifetimeValue,
        nextPayment
      } = restaurantService.getFinancialSummary(restaurant);
      setSummaryData({
        ...summaryData,
        pendingPayout: nextPayment,
        totalEarnings: lifetimeValue,
        totalPaid: lifetimePaid
      })
    }
    fetchBilling()
  }, [restaurant])

  const exportTransactionsCSV = async () => {
    if (!restaurant) { return }
    setIsLoadingExportTransactionsCSV(true);
    const response = await getOrdersCsvDownloadUrl(restaurant.id)
    if (!response.success) {
      toast.error("There was an error exporting your transactions to CSV");
    }
    setIsLoadingExportTransactionsCSV(false);
    if (response.downloadUrl) {
      window.open(response.downloadUrl, '_blank');
    }
  }

  const exportPayoutsCSV = async (payouts: Payout[]) => {
    downloadPayoutsCSV(payouts)
  }

  const chartData = buildCumulativeChartData(transactions)

  const summaryCards = [
    { label: 'Total Earnings', value: Math.round(summaryData.totalEarnings)/100, icon: '💰' },
    { label: 'Total Paid To Restaurant', value: Math.round(summaryData.totalPaid)/100, icon: '📅' }, // TODO : Lets come back to this we may need if we pay weekly
    { label: 'Pending Payout', value: Math.round(summaryData.pendingPayout)/100, icon: '⏳' },
    { label: 'Next Payout', value: summaryData.nextPayoutDate, icon: '📤' },
  ];
  
  return (
    <Box style={{ padding: 32, minHeight: '100vh', display: "flex", flexDirection: "column"}} className={"page-container"}>
      <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {summaryCards.map((card) => (
          <Card key={card.label} elevation={4} style={{ transition: 'transform 0.2s', cursor: 'pointer' }} onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.02)')} onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}>
            <CardHeader
              avatar={<span style={{ fontSize: 24 }}>{card.icon}</span>}
              title={<Typography variant="subtitle2" color="textSecondary">{card.label}</Typography>}
            />
            <CardContent>
              <Typography variant="h4">{typeof card.value === 'number' ? `${formatMoney(card.value)}` : card.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card elevation={4} style={{ marginTop: 32, overflow: 'visible' }}>
        {/* TODO: NEED TO CLEAN THIS UP */}
        <CardHeader
          title={<Typography variant="h6">Income Over Time</Typography>}
          action={<Button variant="outlined" onClick={exportTransactionsCSV} startIcon={<DownloadIcon />}>Export CSV</Button>}
        />
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }} width={50}>
              <defs>
                <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34D399" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#34D399" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" />
              <XAxis dataKey="date" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#10B981"
                strokeWidth={3}
                fill="url(#colorNet)"
                dot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card elevation={4} style={{ marginTop: 32 }}>
        <CardHeader
          title={<Typography variant="h6">Orders & Refunds</Typography>}
          action=
          {

            isLoadingExportTransactionsCSV ?
              <CircularProgress size={24} sx={{ color: "black" }} />
              :
              <Button variant="outlined" onClick={exportTransactionsCSV}>
                <DownloadIcon />Export CSV
              </Button>
          }
        />
        <CardContent>
          <TransactionsTable transactions={transactions} loadingTransaction={loadingTransactions}/>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </div>
        </CardContent>
      </Card>

      <Card elevation={4} style={{ marginTop: 32 }}>
        <CardHeader
          title={<Typography variant="h6">Payout History</Typography>}
          action={<Button variant="outlined" onClick={() => exportPayoutsCSV(payouts)}>
            <DownloadIcon />Export CSV
          </Button>}
        />
        <CardContent>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#eeeeee' }}>
                <tr>
                  {['Date', 'Payout ID', 'Period', 'Amount', 'Status'].map((h) => (
                    <th key={h} style={{ padding: '12px', fontSize: 12, textTransform: 'uppercase', color: '#616161' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9f9f9')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
                    <td style={{ padding: '12px', fontSize: 14 }}>{p.date}</td>
                    <td style={{ padding: '12px', fontSize: 14 }}>{p.id}</td>
                    <td style={{ padding: '12px', fontSize: 14 }}>{`${p.periodStart} to ${p.periodEnd}`}</td>
                    <td style={{ padding: '12px', fontSize: 14, textAlign: 'right' }}>{formatMoney(p.amount)}</td>
                    <td style={{ padding: '12px', fontSize: 14, textAlign: 'center' }}>
                      <Chip label={p.status} color={
                        p.status === 'Paid' ? 'primary' : p.status === 'Pending' ? 'warning' : 'error'
                      } size="small" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </Box>
  );
};